/*
  <bloom-stories> — PDP "stories" rail + full-screen popup player.
  Circular thumbnails (rendered server-side) open an Instagram-stories-style
  modal that autoplays merchant-uploaded videos (Shopify-hosted mp4 or a
  YouTube/Vimeo embed), with segmented progress, tap/keyboard/swipe navigation,
  a mute toggle and an optional "shop this" CTA. Progressive: with no JS the
  thumbnails are still meaningful images; the modal is built on first open.
  Follows the Bloom custom-element + no-bundler convention.
*/
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const EMBED_FALLBACK_SECONDS = 30; // YouTube/Vimeo can't report time — auto-advance after this

class BloomStories extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.modal = null;
    this.items = [];
    this.loops = [];
    this.timer = null;
    this.muted = false;
    this.opener = null;
    this._onKey = this._onKey.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
  }

  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-story-open]'));
    this.items = this.buttons.map((b) => ({
      label: b.dataset.label || '',
      caption: b.dataset.caption || '',
      video: b.dataset.video || '',
      embed: b.dataset.embed || '',
      poster: b.dataset.poster || '',
      duration: parseFloat(b.dataset.duration) || 6,
      link: b.dataset.link || '',
      linkLabel: b.dataset.linkLabel || '',
    }));
    this.buttons.forEach((b, i) => {
      b.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(i);
      });
    });
    this._initRail();
  }

  disconnectedCallback() {
    if (this._railObserver) this._railObserver.disconnect();
    document.removeEventListener('visibilitychange', this._onRailVisibility);
  }

  /* ---------- Ambient rail: the circles play their own loop, unopened ---------- */
  _initRail() {
    // Only adopt the loops once autoplay is actually on — _playRail() runs on modal
    // close too, and an empty list is what keeps it a no-op when it isn't.
    if (this.dataset.autoplay !== 'true' || REDUCED_MOTION) return;
    this.loops = Array.from(this.querySelectorAll('[data-story-loop]'));
    if (!this.loops.length) return;

    this._railPlaying = false;
    this._onRailVisibility = () => {
      if (document.hidden) this._pauseRail();
      else if (this._railVisible) this._playRail();
    };
    document.addEventListener('visibilitychange', this._onRailVisibility);

    // Sources are attached lazily so four videos never touch the critical path.
    this._railObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this._railVisible = entry.isIntersecting;
          if (entry.isIntersecting) this._playRail();
          else this._pauseRail();
        });
      },
      { rootMargin: '150px 0px', threshold: 0.01 }
    );
    this._railObserver.observe(this);
  }

  _playRail() {
    if (this._modalOpen) return;
    this.loops.forEach((video) => {
      if (!video.src && video.dataset.src) video.src = video.dataset.src;
      video.muted = true;
      const played = video.play();
      if (played && typeof played.then === 'function') {
        played
          .then(() => video.closest('.bloom-stories__item')?.classList.add('is-playing'))
          .catch(() => {
            /* Autoplay refused (data saver, low power mode) — the poster stays. */
          });
      }
    });
  }

  _pauseRail() {
    this.loops.forEach((video) => {
      if (!video.paused) video.pause();
    });
  }

  static playable(item) {
    return !!(item && (item.video || item.embed || item.poster));
  }

  open(index) {
    // Advance to the first playable story from the requested index.
    let i = index;
    while (i < this.items.length && !BloomStories.playable(this.items[i])) i += 1;
    if (i >= this.items.length) return;
    this.opener = this.buttons[index] || document.activeElement;
    this._modalOpen = true;
    this._pauseRail();
    this.index = i;
    if (!this.modal) this._build();
    document.body.appendChild(this.modal);
    // Lock scroll without layout shift.
    this._scrollLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKey);
    document.addEventListener('visibilitychange', this._onVisibility);
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
      this._render();
      const closeBtn = this.modal.querySelector('[data-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    });
  }

  close() {
    this._modalOpen = false;
    if (this._railVisible) this._playRail();
    if (!this.modal) return;
    this._stopMedia();
    this.modal.classList.remove('is-open');
    document.removeEventListener('keydown', this._onKey);
    document.removeEventListener('visibilitychange', this._onVisibility);
    document.body.style.overflow = this._scrollLock || '';
    const modal = this.modal;
    const done = () => {
      if (modal.parentNode) modal.parentNode.removeChild(modal);
      modal.removeEventListener('transitionend', done);
    };
    if (REDUCED_MOTION) done();
    else modal.addEventListener('transitionend', done);
    if (this.opener && typeof this.opener.focus === 'function') {
      this.opener.focus({ preventScroll: true });
    }
  }

  next() {
    let i = this.index + 1;
    while (i < this.items.length && !BloomStories.playable(this.items[i])) i += 1;
    if (i >= this.items.length) {
      this.close();
      return;
    }
    this.index = i;
    this._render();
  }

  prev() {
    let i = this.index - 1;
    while (i >= 0 && !BloomStories.playable(this.items[i])) i -= 1;
    if (i < 0) {
      this._render(); // restart current
      return;
    }
    this.index = i;
    this._render();
  }

  /* ---------- DOM ---------- */
  _build() {
    const modal = document.createElement('div');
    modal.className = 'bloom-stories-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product videos');
    modal.innerHTML = `
      <div class="bloom-stories-modal__backdrop" data-close></div>
      <button class="bloom-stories-modal__nav bloom-stories-modal__nav--prev" data-prev type="button" aria-label="Previous video">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>
      </button>
      <div class="bloom-stories-modal__card">
        <div class="bloom-stories-modal__progress" data-progress></div>
        <div class="bloom-stories-modal__top">
          <span class="bloom-stories-modal__label" data-label></span>
          <div class="bloom-stories-modal__ctrls">
            <button class="bloom-stories-modal__ctrl" data-mute type="button" aria-label="Toggle sound"></button>
            <button class="bloom-stories-modal__ctrl" data-close type="button" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </div>
        </div>
        <div class="bloom-stories-modal__media" data-media></div>
        <button class="bloom-stories-modal__zone bloom-stories-modal__zone--prev" data-prev type="button" tabindex="-1" aria-hidden="true"></button>
        <button class="bloom-stories-modal__zone bloom-stories-modal__zone--next" data-next type="button" tabindex="-1" aria-hidden="true"></button>
        <div class="bloom-stories-modal__foot">
          <p class="bloom-stories-modal__caption" data-caption hidden></p>
          <a class="bloom-stories-modal__cta" data-cta hidden></a>
        </div>
      </div>
      <button class="bloom-stories-modal__nav bloom-stories-modal__nav--next" data-next type="button" aria-label="Next video">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
      </button>`;

    modal.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    modal.querySelectorAll('[data-prev]').forEach((el) =>
      el.addEventListener('click', () => this.prev())
    );
    modal.querySelectorAll('[data-next]').forEach((el) =>
      el.addEventListener('click', () => this.next())
    );
    modal.querySelector('[data-mute]').addEventListener('click', () => this._toggleMute());

    // Build one progress segment per playable story.
    const progress = modal.querySelector('[data-progress]');
    this.items.forEach((item) => {
      const seg = document.createElement('span');
      seg.className = 'bloom-stories-modal__seg';
      if (!BloomStories.playable(item)) seg.style.display = 'none';
      seg.innerHTML = '<i></i>';
      progress.appendChild(seg);
    });

    this._bindSwipe(modal.querySelector('.bloom-stories-modal__card'));
    this.modal = modal;
    this._updateMuteIcon();
  }

  _render() {
    this._stopMedia();
    const item = this.items[this.index];
    const media = this.modal.querySelector('[data-media]');
    const label = this.modal.querySelector('[data-label]');
    const caption = this.modal.querySelector('[data-caption]');
    const cta = this.modal.querySelector('[data-cta]');
    media.innerHTML = '';
    label.textContent = item.label || '';

    // Progress segment states.
    const segs = this.modal.querySelectorAll('.bloom-stories-modal__seg');
    segs.forEach((seg, i) => {
      const bar = seg.querySelector('i');
      bar.style.transition = 'none';
      bar.style.width = i < this.index ? '100%' : '0%';
    });

    if (item.video) {
      const video = document.createElement('video');
      video.className = 'bloom-stories-modal__video';
      video.src = item.video;
      if (item.poster) video.poster = item.poster;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'auto';
      video.muted = this.muted;
      video.addEventListener('ended', () => this.next());
      video.addEventListener('timeupdate', () => {
        if (!video.duration) return;
        const bar = segs[this.index] && segs[this.index].querySelector('i');
        if (bar) {
          bar.style.transition = 'none';
          bar.style.width = `${Math.min(100, (video.currentTime / video.duration) * 100)}%`;
        }
      });
      media.appendChild(video);
      this.currentMedia = video;
      const attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(() => {
          // Autoplay with sound blocked — fall back to muted playback.
          video.muted = true;
          this.muted = true;
          this._updateMuteIcon();
          video.play().catch(() => {});
        });
      }
    } else if (item.embed) {
      const frame = document.createElement('iframe');
      frame.className = 'bloom-stories-modal__frame';
      frame.src = item.embed;
      frame.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
      frame.setAttribute('allowfullscreen', '');
      frame.setAttribute('title', item.label || 'Video');
      media.appendChild(frame);
      this.currentMedia = frame;
      this._runTimedProgress(segs[this.index], EMBED_FALLBACK_SECONDS);
    } else if (item.poster) {
      const img = document.createElement('img');
      img.className = 'bloom-stories-modal__img';
      img.src = item.poster;
      img.alt = item.label || '';
      media.appendChild(img);
      this.currentMedia = img;
      this._runTimedProgress(segs[this.index], item.duration);
    }

    if (item.caption) {
      caption.textContent = item.caption;
      caption.hidden = false;
    } else {
      caption.hidden = true;
    }
    if (item.link) {
      cta.href = item.link;
      cta.textContent = item.linkLabel || 'Shop this style';
      cta.hidden = false;
    } else {
      cta.hidden = true;
    }

    // Only one active mute button is meaningful for embeds.
    this.modal.querySelector('[data-mute]').style.display = item.video ? '' : 'none';
  }

  // Animate a progress segment over `seconds`, then advance (images + embeds).
  _runTimedProgress(seg, seconds) {
    if (!seg) return;
    const bar = seg.querySelector('i');
    if (REDUCED_MOTION) {
      bar.style.transition = 'none';
      bar.style.width = '100%';
    } else {
      bar.style.transition = 'none';
      bar.style.width = '0%';
      // Force reflow so the transition runs from 0.
      void bar.offsetWidth;
      bar.style.transition = `width ${seconds}s linear`;
      bar.style.width = '100%';
    }
    this.timer = window.setTimeout(() => this.next(), seconds * 1000);
  }

  _stopMedia() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.currentMedia) {
      if (this.currentMedia.tagName === 'VIDEO') {
        this.currentMedia.pause();
        this.currentMedia.removeAttribute('src');
        this.currentMedia.load();
      } else if (this.currentMedia.tagName === 'IFRAME') {
        this.currentMedia.src = 'about:blank';
      }
      this.currentMedia = null;
    }
  }

  _toggleMute() {
    this.muted = !this.muted;
    if (this.currentMedia && this.currentMedia.tagName === 'VIDEO') {
      this.currentMedia.muted = this.muted;
      if (!this.muted) this.currentMedia.play().catch(() => {});
    }
    this._updateMuteIcon();
  }

  _updateMuteIcon() {
    if (!this.modal) return;
    const btn = this.modal.querySelector('[data-mute]');
    const on = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16 9a3 3 0 0 1 0 6"/><path d="M19 6a7 7 0 0 1 0 12"/></svg>`;
    const off = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>`;
    btn.innerHTML = this.muted ? off : on;
    btn.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
  }

  _onKey(e) {
    switch (e.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case 'ArrowLeft':
        this.prev();
        break;
      case 'Tab':
        this._trapFocus(e);
        break;
      default:
        break;
    }
  }

  _trapFocus(e) {
    const focusable = this.modal.querySelectorAll(
      'button:not([tabindex="-1"]), a[href]'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _onVisibility() {
    if (!this.currentMedia) return;
    if (document.hidden) {
      if (this.currentMedia.tagName === 'VIDEO') this.currentMedia.pause();
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    } else if (this.currentMedia.tagName === 'VIDEO') {
      this.currentMedia.play().catch(() => {});
    }
  }

  _bindSwipe(el) {
    let x0 = null;
    let y0 = null;
    el.addEventListener(
      'touchstart',
      (e) => {
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      },
      { passive: true }
    );
    el.addEventListener(
      'touchend',
      (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dy) > Math.abs(dx) && dy > 70) this.close();
        else if (Math.abs(dx) > 45) (dx < 0 ? this.next() : this.prev());
        x0 = null;
        y0 = null;
      },
      { passive: true }
    );
  }
}

if (!customElements.get('bloom-stories')) {
  customElements.define('bloom-stories', BloomStories);
}
